import { useMemo, useState, type ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { RebateAnalyticsCollapseToggle } from '@/components/rebates/RebateAnalyticsCollapseToggle';
import type { PurchaseTrendCell } from '@erp/rpc';
import { formatCurrency, serializeMoney } from '@erp/shared-utils';
import BreakdownTable, {
  type BreakdownCellContext,
} from '@/components/common/breakdown-tables/BreakdownTable';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { PurchaseChartStatus } from './purchaseTrendPage';
import {
  aggregatePurchaseTrendValues,
  mapPurchaseTrendCellsToBreakdown,
  purchaseTrendColumnOrder,
  purchaseTrendSubColumnOrder,
  type PurchaseTrendTableLabels,
  type PurchaseTrendTableView,
} from './purchaseTrendTables';

function dashPlaceholder() {
  return (
    <span className="text-muted-foreground">
      <T context="empty purchases vs rebates table cell">-</T>
    </span>
  );
}

function renderPurchaseTrendCell(context: BreakdownCellContext) {
  const value = context.cell?.value;
  if (value === null || value === undefined) {
    return dashPlaceholder();
  }
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return dashPlaceholder();
  }
  return formatCurrency(serializeMoney(value));
}

export function usePurchaseTrendTableLabels(): PurchaseTrendTableLabels {
  const gt = useGT();
  return useMemo(
    () => ({
      purchases: gt('Purchases', {
        context: 'purchases vs rebates table column',
      }),
      rebates: gt('Rebates', {
        context: 'purchases vs rebates table column',
      }),
      ytd: gt('YTD', {
        context: 'year to date column on purchases vs rebates table',
      }),
      notInGently: gt('In file, not in Gently', {
        context: 'label for unmatched purchase file supplier or division',
      }),
    }),
    [gt],
  );
}

export function PurchaseTrendTablesSection({
  status,
  cells,
  view,
  onViewChange,
  actions,
}: {
  status: PurchaseChartStatus;
  cells: PurchaseTrendCell[];
  view: PurchaseTrendTableView;
  onViewChange: (view: PurchaseTrendTableView) => void;
  actions?: ReactNode;
}) {
  const gt = useGT();
  const [collapsed, setCollapsed] = useState(false);
  const labels = usePurchaseTrendTableLabels();
  const data = useMemo(
    () => mapPurchaseTrendCellsToBreakdown(cells, view, labels),
    [cells, labels, view],
  );
  const rowLabel =
    view === 'divisions'
      ? gt('Division', {
          context: 'purchases vs rebates table row header',
        })
      : gt('Supplier', {
          context: 'purchases vs rebates table row header',
        });

  return (
    <Card className="mt-3" data-testid="purchase-trend-tables">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-1.5">
            <RebateAnalyticsCollapseToggle
              isCollapsed={collapsed}
              onToggle={() => setCollapsed((open) => !open)}
              testId="purchase-trend-tables-collapse"
            />
            <T context="purchases vs rebates breakdown table title">
              Purchases vs. Rebates
            </T>
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {actions}
            <Tooltip>
              <TooltipTrigger asChild>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <span className="text-muted-foreground">
                    <T context="label for purchases vs rebates table grouping">
                      Group rows by
                    </T>
                  </span>
                  <span>
                    <T context="purchases vs rebates table view option">
                      Divisions
                    </T>
                  </span>
                  <Switch
                    id="purchase-trend-table-view"
                    data-testid="purchase-trend-table-view"
                    checked={view === 'suppliers'}
                    onCheckedChange={(checked) =>
                      onViewChange(checked ? 'suppliers' : 'divisions')
                    }
                  />
                  <span>
                    <T context="purchases vs rebates table view option">
                      Suppliers
                    </T>
                  </span>
                </label>
              </TooltipTrigger>
              <TooltipContent>
                <T context="tooltip for purchases vs rebates table grouping">
                  Rows are divisions with suppliers underneath, or suppliers
                  with divisions underneath.
                </T>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </CardHeader>
      {collapsed ? null : (
        <CardContent className="p-0">
          {status === 'loading' ? (
            <div className="p-4">
              <TableSkeleton />
            </div>
          ) : (
            <BreakdownTable
              data={data}
              showCard={false}
              title=""
              rowLabel={rowLabel}
              xDimOrder={purchaseTrendColumnOrder(labels.ytd)}
              xDim2Order={purchaseTrendSubColumnOrder(
                labels.purchases,
                labels.rebates,
              )}
              expandAllColumns
              fillMissingSubColumns
              specialColumns={[labels.ytd]}
              shadedFromColumn={labels.ytd}
              aggregateCellValues={aggregatePurchaseTrendValues}
              renderCell={renderPurchaseTrendCell}
              maxHeight={700}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
}
